import Axios from "axios";
import SparkMD5 from "spark-md5";

import { getAudioMp3 } from "./generalTts";

export interface TtsRequestParameters {
  tts_hash: string;
  language_id: string;
  voice: string;
  text: string;
  speed: string;
  ssml: string;
  creator: string;
  creation_date: string;
  has_speech_annotation: boolean;
  language_voice_text_hash: string;
}

export interface TtsDataResponse {
  tts_request_parameters_items: TtsRequestParameters[];
}

export interface DuolingoTtsConfig {
  supported_voices: Record<string, Record<string, DuolingotTtsVoiceData>>;
}

export type DuolingoTtsProvider =
  | "azure"
  | "google"
  | "ivona"
  | "recorded"
  | "linguatec";

export interface DuolingotTtsVoiceData {
  gender?: string;
  age?: string;
  provider: DuolingoTtsProvider;
  speeds: string[];
  status: string;
  is_recorded: boolean;
}
export type DuolingoTtsStatus =
  | "created"
  | "creating"
  | "placeholder"
  | "not_started"
  | "error"
  | "recorded_voice"
  | "not_submitted";

// given a set of statuses, the status that have the lowest value here will be the status for the set
export const DuolingoTtsStatusPriorities: Record<DuolingoTtsStatus, number> = {
  created: 4,
  creating: 1,
  error: 0,
  not_started: 2,
  not_submitted: 6,
  placeholder: 3,
  recorded_voice: 5,
};

// these statuses will not change
export const finalStatuses = new Set(["error", "created", "recorded_voice"]);

export interface DuolingoTtsGenerationRequest {
  force?: boolean;
  texts: string[];
  voices?: string[];
  speeds?: string[];
  ssml_body?: Record<string, string>;
}

export interface DuolingoTtsData {
  texts: string[];
  voices?: string[];
  speeds?: string[];
  status: DuolingoTtsStatus;
}

export interface DuolingoTtsGenerationResponse {
  eta: Date;
  statuses: Record<string, { status: DuolingoTtsStatus }[]>;
  texts: Record<string, DuolingoTtsStatus>;
}

export interface DuolingoTtsStatusRequest {
  text: string;
  voice: string;
  speed?: string;
  ssml_body?: string;
}

export type DuolingoTtsAnnotationGenerationResponse = {
  text: string;
  destination: string;
}[];

const duolingoTtsClient = Axios.create({
  baseURL: `${process.env.REACT_APP_DUOLINGO_TTS_ORIGIN}`,
  withCredentials: true,
});

export const getDuolingoTtsLink = (voiceName: string, ttsHash: string) =>
  `${
    process.env.REACT_APP_DUOLINGO_TTS_CLOUDFRONT_BUCKET
  }/${voiceName.toLowerCase()}/${ttsHash}`;

export const getDuolingoTtsMp3 = (voiceName: string, ttsHash: string) =>
  getAudioMp3(getDuolingoTtsLink(voiceName, ttsHash));

export const getTtsData = async (
  ttsHashes: string[],
  voice: string,
  text: string,
  onlySearchNormalSpeed: boolean,
): Promise<TtsDataResponse> =>
  (
    await duolingoTtsClient.get<TtsDataResponse>(
      process.env.REACT_APP_USE_EDGE_GATEWAY_DUOLINGO_TTS_ROUTE === "true"
        ? "/2022-01-01/duolingo-tts/tts-request-parameters"
        : "/api/1/tts-request-parameters",
      {
        params: {
          speeds: onlySearchNormalSpeed ? "normal" : "normal,slow",
          text,
          tts_hashes: ttsHashes?.join(","),
          voice,
        },
      },
    )
  ).data;

let getDuolingoTtsConfigMemo: DuolingoTtsConfig;
const getDuolingoTtsConfig = async (): Promise<DuolingoTtsConfig> => {
  if (!getDuolingoTtsConfigMemo) {
    getDuolingoTtsConfigMemo = (
      await Axios.get(
        `${process.env.REACT_APP_DUOLINGO_TTS_CLOUDFRONT_BUCKET}/config/latest`,
      )
    ).data;
  }

  return getDuolingoTtsConfigMemo;
};

const getDuolingoTtsVoiceData = async () =>
  (await getDuolingoTtsConfig()).supported_voices;

export const getDuolingoTtsVoices = async (language?: string) => {
  const allVoiceEntries = Object.entries(await getDuolingoTtsVoiceData());
  const relevantEntires = language
    ? allVoiceEntries.filter(([voiceLanguage]) => language === voiceLanguage)
    : allVoiceEntries;
  const voicesIter = relevantEntires.flatMap(([, voiceDict]) =>
    Object.entries(voiceDict).flatMap(([name, voiceData]) =>
      // Test voices are an implementation detail of the TTS Correction Tool
      // for World voices and should not otherwise be exposed, such as in this Portal.
      voiceData.status === "test"
        ? []
        : [
            {
              name,
              ...voiceData,
            },
          ],
    ),
  );

  return voicesIter ? voicesIter : undefined;
};

export const getDuolingoTtsVoiceNames = async (
  language?: string,
): Promise<string[] | undefined> =>
  (await getDuolingoTtsVoices(language))?.flatMap(voice =>
    voice.status === "test" ? [] : [voice.name],
  );

export const getDuolingoTtsLanguages = async () =>
  Object.keys((await getDuolingoTtsConfig()).supported_voices);

export const getDuolingoTtsRecordedVoices = async () => {
  const allVoiceData = await getDuolingoTtsVoiceData();
  const voicesIter = Object.entries(allVoiceData).flatMap(
    ([language, voiceDict]) =>
      Object.entries(voiceDict).flatMap(([voiceName, voiceData]) =>
        voiceData.is_recorded
          ? { language, name: voiceName, ...voiceData }
          : [],
      ),
  );

  return voicesIter ? voicesIter : undefined;
};

let voiceNameLanguageMapMemo: Record<string, string>;
const getVoiceNameLanguageMapMemo = async () => {
  if (!voiceNameLanguageMapMemo) {
    const allVoiceEntries = Object.entries(await getDuolingoTtsVoiceData());
    voiceNameLanguageMapMemo = Object.fromEntries(
      allVoiceEntries.flatMap(([language, voiceDict]) =>
        Object.keys(voiceDict).map(voiceName => [voiceName, language]),
      ),
    );
  }
  return voiceNameLanguageMapMemo;
};
export const getDuolingoTtsLanguage = async (duolingoTtsVoiceName: string) => {
  const mapping = await getVoiceNameLanguageMapMemo();
  if (mapping[duolingoTtsVoiceName]) {
    return mapping[duolingoTtsVoiceName];
  }
  throw new Error(
    `Language for voice ${duolingoTtsVoiceName} could not be found`,
  );
};

export const generateAnnotationsForExistingTts = async (
  languageId: string,
  text: string,
  ttsHash: string,
  voiceName: string,
) => {
  const requestData = new FormData();
  requestData.append(
    "annotation_requests",
    JSON.stringify([{ text, tts_hash: ttsHash, voice: voiceName }]),
  );
  // If we eventually support asynchronous annotation generation on frontend,
  // make sure to handle a 202 response code with no data in it.
  requestData.append("synchronous", "true");

  try {
    const response =
      await duolingoTtsClient.post<DuolingoTtsAnnotationGenerationResponse>(
        process.env.REACT_APP_USE_EDGE_GATEWAY_DUOLINGO_TTS_ROUTE === "true"
          ? `/2022-01-01/duolingo-tts/${languageId}/annotations`
          : `/api/1/tts/${languageId}/annotations`,
        requestData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
    return response.data;
  } catch (error) {
    if (Axios.isAxiosError(error) && error.response) {
      // Server responded with an error
      throw new Error(
        `TTS annotation generation failed: ${error.response.status} ${error.response.statusText} ${error.response.data}`,
      );
    } else {
      throw error;
    }
  }
};

export const generateAudioWithDuolingoTts = async (
  body: DuolingoTtsGenerationRequest,
  languageId: string,
) => {
  const data = new FormData();

  data.append("consumer_name", "tts-data-web");
  data.append("texts", JSON.stringify(body.texts));
  if (body.force) {
    data.append("force", JSON.stringify(body.force));
  }
  if (body.voices) {
    data.append("voices", JSON.stringify(body.voices));
  }
  if (body.speeds) {
    data.append("speeds", JSON.stringify(body.speeds));
  }
  if (body.ssml_body) {
    data.append("ssml_body", JSON.stringify(body.ssml_body));
  }

  return (
    await duolingoTtsClient.postForm<DuolingoTtsGenerationResponse>(
      process.env.REACT_APP_USE_EDGE_GATEWAY_DUOLINGO_TTS_ROUTE === "true"
        ? `/2022-01-01/duolingo-tts/${languageId}/texts`
        : `/api/1/tts/${languageId}/texts`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    )
  ).data;
};

export const getDuolingoTtsGenerationStatus = async (
  request: DuolingoTtsStatusRequest,
  languageId: string,
) =>
  (
    await duolingoTtsClient.get<DuolingoTtsStatus>(
      process.env.REACT_APP_USE_EDGE_GATEWAY_DUOLINGO_TTS_ROUTE === "true"
        ? `/2022-01-01/duolingo-tts/${languageId}/status`
        : `/api/1/tts/${languageId}/status`,
      { params: request },
    )
  ).data;

export const parseStatus = (response: DuolingoTtsGenerationResponse) => {
  const statuses = Object.keys(response.texts).map(
    (key: string) => response.texts[key],
  );
  const areSomeCreated = statuses.some(status => status === "created");
  const status = statuses.reduce(
    (prevValue, curValue) =>
      DuolingoTtsStatusPriorities[curValue] <
      DuolingoTtsStatusPriorities[prevValue]
        ? curValue
        : prevValue,
    "not_submitted",
  );

  return areSomeCreated && status === "not_started" ? "creating" : status;
};

export const getTtsHash = (
  languageId: string,
  voiceName: string,
  speed: string,
  text: string,
  pronunciation: string | undefined,
) => {
  const spark = new SparkMD5();
  spark.append(languageId).append(voiceName).append(speed).append(text.trim());
  if (pronunciation !== undefined) {
    spark.append(pronunciation);
  }
  return spark.end();
};
